// app/components/ContactForm.tsx
"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Grid from "@mui/material/Grid";
import {
  Typography,
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { useLocale, useTranslations } from "next-intl";
import Image from "next/image";
import { usePathname } from "next/navigation";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": { padding: theme.spacing(2) },
  "& .MuiDialogActions-root": { padding: theme.spacing(1) },
}));

type InlineEdit = {
  id: string;
  type: "text" | "image";
  selector: string;
  value: string;
  cmsKey?: string;
};

function ContactForm({ ...props }) {
  const t = useTranslations("Contact");
  const locale = useLocale();
  const pathname = usePathname() || "/";

  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [customerFullName, setCustomerFullName] = useState("");

  const formRef = useRef<HTMLFormElement | null>(null);
  const submittingRef = useRef(false);

  // path ที่ใช้เก็บใน CMS (ตัด segment locale ออก)
  const pagePath = useMemo(() => {
    const parts = pathname.split("/");
    // รูปแบบ: /en/contact-us หรือ /zh/contact-us
    if (parts[1] === "en" || parts[1] === "zh") {
      const tail = parts.slice(2).join("/");
      return tail ? `/${tail}` : "/";
    }
    // รูปแบบ: / หรือ /contact-us
    return pathname || "/";
  }, [pathname]);

  const handleClose = () => setOpen(false);

  // ---- default copy จาก i18n (SSR friendly) ----
  const defaultFormTitle = t("Form.title");
  const defaultFormSubtitle = t("Form.subtitle");
  const defaultFormTerms = t("Form.terms");
  const defaultSubmit = t("Form.submit");
  const defaultSubmittingLabel = t("Form.submitting");

  const defaultSuccessHi = t("Success.hi");
  const defaultSuccessContent1 = t("Success.content1");
  const defaultSuccessContent2 = t("Success.content2");
  const defaultSuccessPoonphol = t("Success.poonphol");

  // ---- state ที่ใช้แสดงจริง (จะถูก override ด้วย inline edits ถ้ามี) ----
  const [formTitle, setFormTitle] = useState(defaultFormTitle);
  const [formSubtitle, setFormSubtitle] = useState(defaultFormSubtitle);
  const [formTerms, setFormTerms] = useState(defaultFormTerms);
  const [submitLabel, setSubmitLabel] = useState(defaultSubmit);
  const [submittingLabel, setSubmittingLabel] = useState(defaultSubmittingLabel);

  const [successHi, setSuccessHi] = useState(defaultSuccessHi);
  const [successContent1, setSuccessContent1] = useState(defaultSuccessContent1);
  const [successContent2, setSuccessContent2] = useState(defaultSuccessContent2);
  const [successPoonphol, setSuccessPoonphol] =
    useState(defaultSuccessPoonphol);

  // locale เปลี่ยน → reset default จาก i18n ก่อน
  useEffect(() => {
    setFormTitle(defaultFormTitle);
    setFormSubtitle(defaultFormSubtitle);
    setFormTerms(defaultFormTerms);
    setSubmitLabel(defaultSubmit);
    setSubmittingLabel(defaultSubmittingLabel);

    setSuccessHi(defaultSuccessHi);
    setSuccessContent1(defaultSuccessContent1);
    setSuccessContent2(defaultSuccessContent2);
    setSuccessPoonphol(defaultSuccessPoonphol);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale]);

  //
  useEffect(() => {
    const fetchInline = async () => {
      try {
        const res = await fetch(
          `/api/admin/page-inline?websiteId=${process.env.NEXT_PUBLIC_WEBSITE_ID || process.env.WEBSITE_ID}&path=${encodeURIComponent(
            pagePath
          )}&locale=${locale}`,
          {
            credentials: "include",
            cache: "no-store",
          }
        );

        if (!res.ok) return;

        const data = await res.json();
        if (!data?.edits) return;

        const list: InlineEdit[] = data.edits;
        const map: Record<string, string> = {};

        list.forEach((e) => {
          if (e.cmsKey) {
            map[e.cmsKey] = e.value;
            return;
          }
          const match = e.selector?.match(/data-cms-key="([^"]+)"/);
          if (match) {
            map[match[1]] = e.value;
          }
        });

        // ---- apply overrides ตาม key ----
        if (map["contact.form.title"]) setFormTitle(map["contact.form.title"]);
        if (map["contact.form.subtitle"])
          setFormSubtitle(map["contact.form.subtitle"]);
        if (map["contact.form.terms"])
          setFormTerms(map["contact.form.terms"]);
        if (map["contact.form.submit"])
          setSubmitLabel(map["contact.form.submit"]);
        if (map["contact.form.submitting"])
          setSubmittingLabel(map["contact.form.submitting"]);

        if (map["contact.form.success.hi"])
          setSuccessHi(map["contact.form.success.hi"]);
        if (map["contact.form.success.content1"])
          setSuccessContent1(map["contact.form.success.content1"]);
        if (map["contact.form.success.content2"])
          setSuccessContent2(map["contact.form.success.content2"]);
        if (map["contact.form.success.poonphol"])
          setSuccessPoonphol(map["contact.form.success.poonphol"]);
      } catch (err) {
        console.warn("[ContactForm] inline fetch error:", err);
      }
    };

    fetchInline();
  }, [locale, pagePath]);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (submittingRef.current) return;
    submittingRef.current = true;

    const fd = new FormData(e.currentTarget);
    const first = (fd.get("first_name") || "").toString().trim();
    const last = (fd.get("last_name") || "").toString().trim();
    const payload = {
      first_name: first,
      last_name: last,
      email: (fd.get("email") || "").toString().trim(),
      company: (fd.get("company") || "").toString().trim(),
      message: (fd.get("message") || "").toString().trim(),
      consent: fd.get("consent") === "on",
      ppc_hp: (fd.get("ppc_hp") || "").toString().trim(), // honeypot
      locale,
    };

    setCustomerFullName([first, last].filter(Boolean).join(" "));

    try {
      setSubmitting(true);
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        formRef.current?.reset();
        setOpen(true);
      } else {
        console.error(
          "Contact form submit failed",
          await res.json().catch(() => ({}))
        );
      }
    } catch (err) {
      console.error("Failed to submit contact form", err);
    } finally {
      setSubmitting(false);
      submittingRef.current = false;
    }
  };

  return (
    <React.Fragment>
      <Box
        component="form"
        id="contact-form"
        ref={formRef}
        onSubmit={onSubmit}
        autoComplete="off"
        data-aos="fade-up"
        {...props}
        sx={[{
          p: { xs: 3, md: 5 }
        }, ...(Array.isArray(props.sx) ? props.sx : [props.sx])]}>
        <Typography
          variant="h2"
          data-heading-tag="H2"
          data-aos="fade-up"
          data-cms-key="contact.form.title"
          sx={{
            mb: 2,
            whiteSpace: { xs: "normal", lg: "pre-line" }
          }}>
          {formTitle}
        </Typography>

        <Typography
          variant="h3"
          data-heading-tag="H3"
          data-aos="fade-up"
          data-cms-key="contact.form.subtitle"
          sx={{
            mb: 2,
            whiteSpace: { xs: "normal", lg: "pre-line" }
          }}>
          {formSubtitle}
        </Typography>

        {/* Honeypot (hidden). Name and autoComplete token are deliberately
            meaningless: Edge autofill fills name="website" and ignores
            autocomplete="off". */}
        <input
          type="text"
          name="ppc_hp"
          tabIndex={-1}
          autoComplete="ppc-hp-nofill"
          style={{
            position: "absolute",
            left: -9999,
            width: 1,
            height: 1,
            opacity: 0,
          }}
          aria-hidden="true"
        />

        <Grid container spacing={2} className="contact-form">
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              id="first_name"
              name="first_name"
              hiddenLabel
              placeholder={t("Form.first_name")}
              variant="filled"
              fullWidth
              required
              slotProps={{ input: { disableUnderline: true } }}
              className="textfield-input"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              id="last_name"
              name="last_name"
              hiddenLabel
              variant="filled"
              placeholder={t("Form.last_name")}
              fullWidth
              required
              slotProps={{ input: { disableUnderline: true } }}
              className="textfield-input"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              id="email"
              name="email"
              hiddenLabel
              variant="filled"
              placeholder={t("Form.email")}
              type="email"
              autoComplete="email"
              fullWidth
              required
              slotProps={{ input: { disableUnderline: true } }}
              className="textfield-input"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              id="company"
              name="company"
              hiddenLabel
              variant="filled"
              placeholder={t("Form.company")}
              fullWidth
              required
              slotProps={{ input: { disableUnderline: true } }}
              className="textfield-input"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              id="message"
              name="message"
              hiddenLabel
              variant="filled"
              placeholder={t("Form.message")}
              fullWidth
              multiline
              rows={4}
              required
              slotProps={{ input: { disableUnderline: true } }}
              className="textfield-input"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              required
              sx={{ alignItems: "start" }}
              control={
                <Checkbox
                  name="consent"
                  required
                  slotProps={{
                    input: {
                      "data-testid": "privacy-checkbox",
                    } as React.InputHTMLAttributes<HTMLInputElement>,
                  }}
                />
              }
              label={
                <Typography
                  component={"span"}
                  sx={{ fontSize: "0.8rem" }}
                  data-cms-key="contact.form.terms"
                >
                  {formTerms}
                </Typography>
              }
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              fullWidth
              className="submit-button"
              disabled={submitting}
              data-cms-key="contact.form.submit"
            >
              {submitting ? submittingLabel : submitLabel}
            </Button>

            <BootstrapDialog
              onClose={handleClose}
              aria-labelledby="customized-dialog-title"
              open={open}
            >
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: theme.palette.grey[500],
                })}
              >
                <CloseIcon />
              </IconButton>
              <DialogContent dividers>
                <Grid
                  container
                  spacing={{ xs: 3, md: 5, lg: 10 }}
                  className="contact-success"
                  sx={{
                    my: 5,
                    mx: { xs: 0, md: 3 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}>
                  <Grid size={{ xs: 12, md: "auto" }}>
                    <Image
                      src="/assets/email-success.png"
                      alt="email success"
                      width={237}
                      height={291}
                      className="img-fluid"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: "grow" }}>
                    <Typography variant="h5" data-heading-tag="H5" sx={{
                      mb: 2
                    }}>
                      <span data-cms-key="contact.form.success.hi">
                        {successHi}
                      </span>{" "}
                      {customerFullName},
                    </Typography>
                    <Typography
                      variant="body1"
                      data-cms-key="contact.form.success.content1"
                      sx={{
                        mb: 2
                      }}
                    >
                      {successContent1}
                    </Typography>
                    <Typography
                      variant="body1"
                      data-cms-key="contact.form.success.content2"
                      sx={{
                        mb: 2
                      }}
                    >
                      {successContent2}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      data-cms-key="contact.form.success.poonphol"
                    >
                      {successPoonphol}
                    </Typography>
                  </Grid>
                </Grid>
              </DialogContent>
            </BootstrapDialog>
          </Grid>
        </Grid>
      </Box>
    </React.Fragment>
  );
}

export default ContactForm;